import React, { FC, useEffect, useState } from "react";
import { SemibigHeader } from "@narvar/nth-bit-header";
import Paragraph from "@narvar/nth-bit-paragraph";
import TextInput, { SupportedInputType } from "@narvar/nth-block-text-input";
import { useSettings, withSettingsProvider } from "@narvar/nth-hook-settings";
import { useTheme, withThemeProvider } from "@narvar/nth-hook-theme";
import Panel from "@narvar/nth-block-panel";
import PanelHeader from "@narvar/nth-bit-panel-header";
import Button from "@narvar/nth-block-button";
import Divider from "@narvar/nth-bit-divider";
import Steps from "./Steps";
import {
  ErrorBanner,
  Language,
  LocationCard,
  ItemCardSkeleton,
  LineItemSkeleton,
  ItemCard,
  Spinner,
  TextLink,
} from "../ReviewSummary/components";

import { ConfirmationRawProps } from "./types";
import { themeTokens } from "./tokens";
import { useStyles } from "./ConfirmationStyles";
import PreferencesStatus from "../ReviewSummary/components/Preferences/PreferencesStatus";
import { EMAIL_ADDRESS_PATTERN, validatePattern } from "../ReviewSummary/utils";
import { useLanguage } from "@narvar/nth-kit-returns-headless";
import { generateTokensFromObject } from "@narvar/nth-kit-returns-headless/dist/src/hooks/language/useLanguage";
import { getItemAttrs } from "../ReviewSummary/utils/itemSelectorHelper";

const ConfirmationRawC: FC<ConfirmationRawProps> = ({
  error,
  loading,
  shipment,
  type,
  providerMoniker,
  email,
  location,
  alternateEmailToggle = false,
  noInstructions,
  onSubmit,
  newEmailStatus,
  preferencesStatus,
  isHipaaCompliant,
  returnSteps = [],
  locale,
  includeCurrency,
}) => {
  const { itemDisplay } = useSettings();
  const styles = useStyles();
  const [showAlternateEmailForm, setAlternateEmailForm] = useState(false);
  const [emailAddress, setUpdatedEmail] = useState("");
  const [validationError, setValidationError] = useState(false);
  const { items, returnLabels } = shipment || {};
  const mappedUrls: { qrCodeUrl?: string; printLabelUrl?: string } =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    returnLabels?.reduce((acc: any, { labelType, labelUrl }: any) => {
      if (labelType === "QR_CODE") {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return {
          ...acc,
          qrCodeUrl: labelUrl,
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return {
        ...acc,
        printLabelUrl: labelUrl,
      };
    }, {}) || {};
  const { qrCodeUrl, printLabelUrl } = mappedUrls;

  const { value: provider } = useLanguage({
    langKey: `returnsMoniker${providerMoniker ?? ""}DisplayName`,
    asString: true,
  });
  const { rawValue: skuLabel } = useLanguage({
    langKey: "returnsItemListItemInfoskuLabel",
  });
  const { rawValue: qtyLabel } = useLanguage({
    langKey: "returnsItemListItemInfoqtyLabel",
  });
  const { rawValue: colorLabel } = useLanguage({
    langKey: "returnsItemListItemInfocolorLabel",
  });
  const { rawValue: sizeLabel } = useLanguage({
    langKey: "returnsItemListItemInfosizeLabel",
  });
  const { rawValue: reasonLabel } = useLanguage({
    langKey: "returnsItemListItemInforeasonLabel",
  });
  const { rawValue: placeholder } = useLanguage({
    langKey: "returnsConfirmationActionsPanelEmailInputPlaceholder",
  });

  const {
    linksNoneDefault,
    linksNoneHover,
    buttonsPrimaryDefault,
    buttonsPrimaryHover,
    bodytextDefaultNone,
  } = useTheme();

  useEffect(() => {
    if (newEmailStatus?.status === "success") {
      setAlternateEmailForm(false);
      setUpdatedEmail("");
    }
  }, [newEmailStatus?.status]);

  if (loading) {
    return (
      <div>
        <ItemCardSkeleton />
        <LineItemSkeleton />
        <Divider />
        <LineItemSkeleton />
        <Divider />
        <LineItemSkeleton />
        <Divider />
        <LineItemSkeleton />
        <ItemCardSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...bodytextDefaultNone }}>
        <ErrorBanner>
          <Language langKey="returnsConfirmationError" />
        </ErrorBanner>
      </div>
    );
  }

  const toggleAlternateEmailForm = () => {
    setAlternateEmailForm(!showAlternateEmailForm);
  };

  const submitAlternateEmailForm = () => {
    const validEmail = validatePattern(
      emailAddress ?? "",
      EMAIL_ADDRESS_PATTERN,
    );
    if (validEmail) {
      setValidationError(false);
      onSubmit?.({
        event: "submit",
        area: "returnConfirmation",
        subComponent: "emailSubmittedReturnDetails",
        value: { emailAddress },
      });
    } else {
      setValidationError(true);
    }
  };

  const langKeySuffix = isHipaaCompliant ? "Hipaa" : "";

  return (
    <div style={{ ...bodytextDefaultNone }}>
      {preferencesStatus && <PreferencesStatus {...preferencesStatus} />}
      <Panel
        header={
          <PanelHeader>
            <Language
              langKey={`returnsConfirmation${type ?? ""}ActionsPanelHeader`}
            />
          </PanelHeader>
        }>
        <header className={styles.confirmationHeader}>
          <SemibigHeader>
            <Language
              langKey={`returnsConfirmation${
                type ?? ""
              }ActionsPanelTitle${langKeySuffix}`}
              replacementTokens={generateTokensFromObject({
                provider,
              })}
            />
          </SemibigHeader>
          {!qrCodeUrl && printLabelUrl && !isHipaaCompliant && (
            <div className={styles.btnContainer}>
              <TextLink href={printLabelUrl}>
                <Button>
                  <Language
                    langKey={`returnsConfirmation${
                      type ?? ""
                    }ActionsPanelMainButton`}
                  />
                </Button>
              </TextLink>
            </div>
          )}
          <Paragraph>
            <Language
              langKey={`returnsConfirmation${
                type ?? ""
              }ActionsPanelDescription${langKeySuffix}`}
              replacementTokens={generateTokensFromObject({
                email: newEmailStatus?.email ? newEmailStatus?.email : email,
                provider,
              })}
            />
          </Paragraph>
          {alternateEmailToggle && !isHipaaCompliant && (
            <div>
              <TextLink
                linkStyle={{
                  sDefault: {
                    ...linksNoneDefault,
                    color: buttonsPrimaryDefault?.backgroundColor,
                  },
                  sHover: {
                    ...linksNoneHover,
                    color: buttonsPrimaryHover?.backgroundColor,
                  },
                }}
                onClick={toggleAlternateEmailForm}>
                <Language
                  langKey={`returnsConfirmation${
                    type ?? ""
                  }ActionsPanelEmailButton`}
                />
              </TextLink>
              {showAlternateEmailForm && (
                <>
                  <div className={styles.alternateEmailForm}>
                    <TextInput
                      value={emailAddress}
                      onChange={(e) => {
                        setUpdatedEmail(e.target.value);
                      }}
                      placeholder={placeholder}
                      type={SupportedInputType.email}
                    />
                    <Button
                      onClick={submitAlternateEmailForm}
                      disabled={
                        newEmailStatus?.status === "loading" || email === ""
                      }>
                      {newEmailStatus?.status === "loading" ? (
                        <Spinner />
                      ) : (
                        <Language langKey={`returnsActionsSubmit`} />
                      )}
                    </Button>
                  </div>
                  <div>
                    {validationError && (
                      <Language
                        langKey={`returnsConfirmationActionsPanelEmailStatusvalidationerror`}
                      />
                    )}
                    <Language
                      langKey={`returnsConfirmationActionsPanelEmailStatus${
                        newEmailStatus?.status ?? ""
                      }`}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          {qrCodeUrl && !isHipaaCompliant && (
            <div className={styles.imgContainer}>
              <img style={{ width: "100%" }} src={qrCodeUrl} alt="" />
            </div>
          )}
        </header>
      </Panel>
      {location && (
        <Panel
          header={
            <PanelHeader>
              <Language
                langKey={`returnsConfirmation${type ?? ""}LocationPanelHeader`}
              />
            </PanelHeader>
          }>
          <LocationCard location={location} active transparent />
        </Panel>
      )}
      {!noInstructions && (
        <Panel
          header={
            <PanelHeader>
              <Language
                langKey={`returnsConfirmation${
                  type ?? ""
                }InstructionsPanelHeader`}
              />
            </PanelHeader>
          }>
          <main>
            <Steps steps={returnSteps} />
          </main>
        </Panel>
      )}
      <Panel
        header={
          <PanelHeader>
            <Language
              langKey={`returnsConfirmation${type ?? ""}ItemsPanelHeader`}
            />
          </PanelHeader>
        }>
        {// eslint-disable-next-line @typescript-eslint/no-explicit-any
        items?.map((item: any) => (
          <ItemCard
            thumbnail={item.imageUrl}
            title={item.name}
            key={`${item.sku ?? ""}${item.itemId ?? ""}`}
            attributes={getItemAttrs({
              locale,
              includeCurrency,
              item,
              itemDisplay,
              labels: {
                skuLabel,
                qtyLabel,
                colorLabel,
                sizeLabel,
              },
            })}
            additionalInfo={
              item.reason.name && `${reasonLabel ?? ""}${item.reason.name}`
            }
            hasShippingProtection={item.hasShippingProtection}
          />
        ))}
      </Panel>
      {qrCodeUrl && printLabelUrl && (
        <Panel header={null}>
          <SemibigHeader>
            <Language
              langKey={`returnsConfirmation${
                type ?? ""
              }SecondaryActionsPanelTitle`}
            />
          </SemibigHeader>
          <Paragraph>
            <Language
              langKey={`returnsConfirmation${
                type ?? ""
              }SecondaryActionsPanelDescription`}
            />
          </Paragraph>
          <TextLink
            linkStyle={{
              sDefault: {
                ...buttonsPrimaryDefault,
                color: buttonsPrimaryDefault?.backgroundColor || "#000000",
                backgroundColor: "none",
                textTransform: "none",
              },
            }}
            href={printLabelUrl}>
            <Language
              langKey={`returnsConfirmation${
                type ?? ""
              }SecondaryActionsPanelMainButton`}
            />
          </TextLink>
        </Panel>
      )}
    </div>
  );
};

export const ConfirmationRaw = withSettingsProvider(
  withThemeProvider(ConfirmationRawC, {
    themeTokens,
  }),
  {
    settingsRequested: `
      itemDisplay {
        additionalItemAttributeConfig {
          attribute
          value {
            concatKey
            concatPosition
            label
            transform
          }
        }
        itemAttributeConfig
      }
    `,
  },
);
